import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { AuthContext } from '@ryanar/react-auth-provider';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { StatusModal } from 'components/modals';
import { AxiosStatus } from 'api/axiosConfig';
import {
  requiredOnly,
  validationCallback,
  notRequiredValidateEmail,
  notRequiredValidateAccount,
  validatePhone,
} from 'utils/validation';
import { changeFieldValue } from 'utils/formMutators';
import routePaths from 'routes/routePaths';
import api from 'api/api';
import { IFormValues } from './ComplaintForm.model';
import RenderComplaintFields from './ComplaintFields';

type IInvoiceModalProps = {
  open: boolean;
  status: 'success' | 'failure';
  content: React.ReactChild;
  onRetry: () => void;
};

const initInvoiceModalProps: IInvoiceModalProps = {
  open: false,
  status: 'failure',
  content: '',
  onRetry: () => null,
};

const ComplaintForm = () => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [invoiceModalProps, setInvoiceModalProps] = useState<IInvoiceModalProps>(initInvoiceModalProps);

  const { authenticated } = React.useContext(AuthContext);

  const history = useHistory();
  const handleOnSuccess = useCallback(() => {
    history.push(routePaths.dashboard);
  }, [history]);

  const closeStatusModal = useCallback(() => {
    setInvoiceModalProps(initInvoiceModalProps);
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
  }, [handleOnSuccess, submitStatus]);

  const dynamicSchema = (subject: string) => {
    switch (subject) {
      case 'other':
        return yup.object().shape({
          subject: requiredOnly('Subject'),
          otherSubject: requiredOnly('Other Subject'),
          fullname: requiredOnly('Full Name'),
          phone: validatePhone('Phone Number'),
          message: requiredOnly('Message'),
          email: notRequiredValidateEmail('Email'),
          accountNumber: notRequiredValidateAccount('Account Number'),
        });
      default:
        return yup.object().shape({
          subject: requiredOnly('Subject'),
          fullname: requiredOnly('Full Name'),
          phone: validatePhone('Phone Number'),
          message: requiredOnly('Message'),
          email: notRequiredValidateEmail('Email'),
          accountNumber: notRequiredValidateAccount('Account Number'),
        });
    }
  };

  const validate = (values: IFormValues) => validationCallback<unknown>(dynamicSchema(values.subject))(values);

  const intialValues: IFormValues = useMemo(
    () => ({
      subject: '',
      otherSubject: '',
      fullname: '',
      phone: '',
      email: '',
      accountNumber: '',
      message: '',
    }),
    [],
  );

  const onSubmit = useCallback(
    (values: IFormValues) => {
      setSubmitStatus(AxiosStatus.PENDING);
      let subjectTopic = values.subject;

      if (values.subject === 'other' && values.otherSubject) {
        subjectTopic = `other - ${values.otherSubject}`;
      }

      const sendComplaint = {
        subject: subjectTopic,
        fullname: values.fullname,
        phone: values.phone,
        email: values.email,
        accountNumber: values.accountNumber,
        message: values.message,
      };

      api
        .get(
          `support/send/email?&accountNumber=${sendComplaint.accountNumber || ''}
          &senderName=${sendComplaint.fullname}
          &phoneNumber=${sendComplaint.phone}
          &emailAddress=${sendComplaint.email || ''}
          &emailSubject=${sendComplaint.subject}
          &emailBody=${sendComplaint.message}`,
          {},
          false,
          false,
        )
        .then(() => {
          setInvoiceModalProps({
            status: 'success',
            open: true,
            content: 'Complaint has been sent!',
            onRetry: () => {
              closeStatusModal();
            },
          });
          setSubmitStatus(AxiosStatus.SUCCESS);
        })
        .catch(() => {
          setInvoiceModalProps({
            status: 'failure',
            open: true,
            content: 'Something went wrong. Please try again.',
            onRetry: () => {
              closeStatusModal();
            },
          });
          setSubmitStatus(AxiosStatus.FAILURE);
        });
    },
    [closeStatusModal],
  );

  return (
    <>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        mutators={{ changeFieldValue }}
        initialValues={intialValues}
        render={({ handleSubmit, form, values, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <FieldsLeveler label="Complaint Details">
              <RenderComplaintFields form={form} values={values} authenticated={authenticated} />
            </FieldsLeveler>
            <SubmitLeveler>
              <Button type="submit" fullWidth disabled={hasValidationErrors || submitting}>
                Submit
              </Button>
            </SubmitLeveler>
            <StatusModal
              open={invoiceModalProps.open}
              status={invoiceModalProps.status}
              onRetry={invoiceModalProps.onRetry}
              onClose={closeStatusModal}
              onRetryText="Retry"
            >
              {invoiceModalProps.content}
            </StatusModal>
          </form>
        )}
      />
    </>
  );
};

export default ComplaintForm;
